import { Classes, HTMLInputProps, InputGroup, Tag } from '@blueprintjs/core'
import CX from 'classnames'
import * as React from 'react'
import Select, { Creatable } from 'react-select'
import VirtualizedSelect from 'react-virtualized-select'
import { IconName } from '@blueprintjs/icons'
import USAStates from '../constants/USAStates'
import CountrySelect from './CountrySelect'
import NullSafeNumericInput, { NullSafeNumericInputProps } from './NullSafeNumericInput'
import { InputLabel, InputLabelProps } from './inputs/InputLabel'
import config from '../config'
import { debounce } from '../utils'

export const rightElementCurrencyTag = currencyCode => {
  switch (currencyCode) {
    case 'USD':
      return null
    case 'CAD':
      return <Tag minimal>CAD</Tag>
    default:
      return null
  }
}

export const GenericInput: React.FC<any> = (props: any) => {
  const optional = props.optional || false
  const required = props.required || false
  const disabled = props.disabled || false
  const inputType = props.type || 'text'

  const { requiredLabelStyle, requiredLabelIconName, helpText, hidden, ...domSafeProps } = props
  // props.hidden has values like "{{ type != "Pottery" }}". This gets coerced into true,
  // which causes accessibility issues for screenreaders and RTL tests

  return (
    <InputLabel
      title={props.title}
      optional={optional}
      requiredLabelIconName={requiredLabelIconName}
      required={required}
      requiredLabelStyle={requiredLabelStyle}
    >
      <input
        width={150}
        {...domSafeProps}
        className={CX('bp3-input', props.className)}
        type={inputType}
        disabled={disabled}
        style={{ width: props.width }}
        placeholder={props.placeholder}
        value={props.value}
        onChange={e => props.onChange(e.target.value)}
        onBlur={props.onBlur}
      />
    </InputLabel>
  )
}

interface GenericNumericInputProps {
  onChange?: (s) => void
  inputWidth?: number
  optional?: boolean
  requiredLabelStyle?: React.CSSProperties
  requiredLabelIconName?: IconName
}

export const GenericNumericInput: React.FC<GenericNumericInputProps & NullSafeNumericInputProps & HTMLInputProps> = ({
  ...props
}) => {
  const width = props.inputWidth ? { style: { width: props.inputWidth }, className: Classes.FILL } : {}

  const { requiredLabelStyle, requiredLabelIconName, max, onChange, ...childComponentProps } = props
  return (
    <InputLabel
      title={props.title}
      optional={props.optional}
      required={props.required}
      requiredLabelStyle={requiredLabelStyle}
      requiredLabelIconName={requiredLabelIconName}
    >
      <div>
        <NullSafeNumericInput
          min={0}
          max={max || +Infinity}
          selectAllOnFocus
          onValueChange={(_, s) => props.onChange(s)}
          value={props.value}
          {...childComponentProps}
          {...width}
        />
      </div>
    </InputLabel>
  )
}

interface GenericCheckboxProps extends InputLabelProps {
  inputWidth?: number
  value?: any
  disabled?: boolean
  onChange: (newValue) => void
}

export const GenericCheckbox: React.FC<GenericCheckboxProps> = ({ ...props }) => {
  const checked = Boolean(props.value)
  const style = {
    label: {
      maxWidth: 'max-content'
    }
  }

  return (
    <InputLabel
      className={CX('bp3-control bp3-checkbox', { 'bp3-align-right': props.className })}
      optional={props.optional}
      required={props.required}
      requiredLabelStyle={props.requiredLabelStyle}
      requiredLabelIconName={props.requiredLabelIconName}
      style={style.label}
    >
      <div style={{ width: props.inputWidth }}>
        <input
          disabled={props.disabled}
          type="checkbox"
          checked={checked}
          onChange={() => props.onChange(!props.value)}
        />
        <span className="bp3-control-indicator" />
        {props.title}
      </div>
    </InputLabel>
  )
}

export const getLoadableSelectOptions = (stableOrder: boolean, options: any[]) => {
  if (stableOrder) {
    return options
  }

  return [...options].sort((a, b) => {
    // handle cases where label is somehow an array. Will just display as empty in form but keep working.
    // Added to handle imported eBay listing specifics that are an array of strings
    const aIsString = typeof a.label === 'string'
    const bIsString = typeof b.label === 'string'

    if (!aIsString && !bIsString) return 0
    if (!aIsString) return 1
    if (!bIsString) return -1

    return a.label.localeCompare(b.label)
  })
}

interface LoadableSelectProps {
  intent?: string
  matchProp?: 'label' | 'value' | 'any' // "whether to match the value, label or both values of each option when filtering", see https://www.npmjs.com/package/react-select/v/1.2.1#filtering-options
  load: Function
  process: (any) => { value: any; label: any }[]
  raw?: boolean
  onChange: Function
  autoload?: any // really this should be a boolean, but I don't want to touch legacy code that coerces numbers/strings to booleans
  virtualized?: boolean

  // allow this interface to have other types
  [x: string]: any
}

// Creates a select dropdown by wrapping the react-select library.
// We hope to use APISelect more instead of this because native selects are nice.
// But this generates a searchable select, which people like a lot.
const QUERY_LIMIT = 500
export class LoadableSelect extends React.Component<LoadableSelectProps, any> {
  mounted = false

  latestRequestId = 0

  state = {
    loading: false,
    options: [],
    loadingPlaceholder: ''
  }

  debouncedGetData: (searchString?: string) => void

  componentDidMount() {
    this.mounted = true
    this.debouncedGetData = debounce(this.getData, 250)

    if (Array.isArray(this.props.autoload)) {
      const formatted = this.props.process(this.props.autoload)
      this.setState({ options: formatted })
      this.props.onChange?.(formatted)
    } else {
      // No autoload? Fetch QUERY_LIMIT from the DB
      this.getData('', QUERY_LIMIT)
    }
  }

  componentWillUnmount() {
    this.mounted = false
    this.latestRequestId += 1
  }

  componentDidUpdate(prevProps: LoadableSelectProps) {
    const autoloadChangedToTrue = prevProps.autoload === false && this.props.autoload === true
    const alreadyFetchedOptions = this.state.options.length > 0

    if (!alreadyFetchedOptions && autoloadChangedToTrue) {
      this.getData(undefined, QUERY_LIMIT)
    }
  }

  onInputChange = (searchString: string) => {
    if (this.state.options.length === 0) {
      this.setState({ loading: true, loadingPlaceholder: 'Loading...' })
    }
    this.debouncedGetData(searchString)
    return searchString
  }

  getData = async (searchString?: string, limit?: number) => {
    if (!this.mounted) return

    const requestId = this.latestRequestId + 1
    this.latestRequestId = requestId

    this.setState({ loading: true, loadingPlaceholder: 'Loading...' })
    try {
      const res = await this.props.load(searchString, limit)
      if (!this.mounted || requestId !== this.latestRequestId) return

      const fetched = this.props.raw ? this.props.process(res) : this.props.process(res.data)

      const selectedValues = Array.isArray(this.props.value) ? this.props.value : [this.props.value]
      const selectedOptions = selectedValues
        .filter(val => {
          if (val === null || val === undefined) return false
          if (typeof val === 'object') {
            const primitiveValue = val.value
            return primitiveValue !== null && primitiveValue !== undefined && primitiveValue !== ''
          }
          return true
        })
        .map(val => {
          const primitiveValue = typeof val === 'object' ? val.value : val
          return (
            fetched.find(opt => String(opt.value) === String(primitiveValue)) ||
            (typeof val === 'object' ? val : { value: val, label: val })
          )
        })

      const merged = [...fetched]
      selectedOptions.forEach(opt => {
        if (!merged.some(o => String(o.value) === String(opt.value))) {
          merged.push(opt)
        }
      })

      this.setState({
        options: merged,
        loading: false
      })
    } catch (_error) {
      if (!this.mounted || requestId !== this.latestRequestId) return

      this.setState({
        options: [],
        loading: false
      })
    }
  }

  onFocus = () => {
    this.getData('', QUERY_LIMIT)
    if (this.props.onFocus !== undefined) this.props.onFocus()
  }

  onBlur = () => {
    if (this.props.onBlur !== undefined) this.props.onBlur()
  }

  render() {
    const { loading, loadingPlaceholder } = this.state
    if (loading && this.props.hideIfEmpty) return null
    if (!loading && !this.state.options && !this.state.options.length && this.props.hideIfEmpty) return null

    // if virtualized, use a special component
    let El = this.props.virtualized ? VirtualizedSelect : Select

    if (config.TEST) {
      // RTL can't see virtualized options
      El = Select
    }

    // if createable, and not virtualized
    // override the regular select component with the createable component
    // the "createable" var below will be ignore unless virtualized
    if (this.props.createable) {
      if (!this.props.virtualized) {
        El = Creatable
      }
    }

    const createable = this.props.createable ? { selectComponent: Creatable } : {}

    if (this.props.noLabel) {
      return (
        <El
          {...this.props}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          ignoreAccents={this.props.ignoreAccents}
          {...createable}
          placeholder={loading ? loadingPlaceholder : this.props.placeholder || 'Select'}
          options={getLoadableSelectOptions(this.props.stableOptionOrder, this.state.options)}
          noResultsText={loading ? 'Loading...' : 'No results'}
          value={this.props.value}
          simpleValue={this.props.simpleValue !== false}
          onChange={this.props.onChange}
          className={CX(this.props.className, this.props.intent === 'danger' && 'loadable-select-danger-intent')}
          autoload={this.props.autoload}
        />
      )
    }

    // Notice that ReactSelect has a default value of matchProp="any". Though we haven't specified that here explicitly,
    // some use cases involve the user typing in the ID of the option (not the string we display) to pull up results,
    // such as scanning an inventory location barcode. So those cases depend on matchProp="any".

    return (
      <InputLabel
        className={CX(
          'loadable-select-label',
          this.props.additionalClassName,
          this.props.intent === 'danger' && 'loadable-select-danger-intent'
        )}
        title={this.props.title}
        optional={this.props.optional}
        required={this.props.required}
        style={this.props.style}
      >
        <El
          {...this.props}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          ignoreAccents={this.props.ignoreAccents}
          {...createable}
          placeholder={loading ? loadingPlaceholder : this.props.placeholder || 'Select'}
          options={getLoadableSelectOptions(this.props.stableOptionOrder, this.state.options)}
          noResultsText={loading ? 'Loading...' : 'No results'}
          value={this.props.value}
          simpleValue={this.props.simpleValue !== false}
          onChange={this.props.onChange}
          onInputChange={this.onInputChange}
        />
      </InputLabel>
    )
  }
}

export const USAStatesSelect = ({
  value,
  onChange,
  disabled,
  style,
  className = null,
  useAbbreviation,
  fill = null
}) => {
  const options = [{ name: 'Select a State', abbreviation: '' }, ...USAStates]
  return (
    <div className={CX({ 'bp3-select': true, 'bp3-fill': fill })}>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        style={style}
        className={className}
      >
        {options.map(s => (
          <option value={useAbbreviation ? s.abbreviation : s.name} key={s.abbreviation}>
            {s.name}
          </option>
        ))}
      </select>
    </div>
  )
}

export const AddressInputGroup = props => {
  const makeValidationClass = field => `bp3-input ${props.showValidations && !field ? 'bp3-intent-danger' : null}`
  const showStateSelector = !props.country || props.country === 'US'

  return (
    <div>
      <label className="bp3-label">
        Street Address
        <input
          placeholder="123 Fake st"
          disabled={props.disabled}
          value={props.street || ''}
          onChange={e => props.onChange('Street', e.target.value)}
          className={makeValidationClass(props.street)}
          style={{ width: '602px', display: 'block', marginBottom: '5px' }}
        />
      </label>
      <div style={{ display: 'flex' }}>
        <label className="bp3-label">
          City
          <input
            placeholder="McLean"
            disabled={props.disabled}
            style={{ width: '246px', marginRight: '5px' }}
            value={props.city || ''}
            className={makeValidationClass(props.city)}
            onChange={e => props.onChange('City', e.target.value)}
          />
        </label>
        <label className="bp3-label">
          <div>State</div>
          {showStateSelector ? (
            <USAStatesSelect
              disabled={props.disabled}
              value={props.state || ''}
              useAbbreviation={props.useAbbreviatedState}
              style={{
                border: props.showValidations && !props.state ? '1px solid red' : 'none',
                width: '196px',
                marginRight: '5px'
              }}
              onChange={v => props.onChange('State', v)}
            />
          ) : (
            <InputGroup value={props.state} onChange={e => props.onChange('State', e.target.value)} />
          )}
        </label>
        <label className="bp3-label">
          Zipcode
          <div>
            <input
              placeholder="Zipcode"
              disabled={props.disabled}
              style={{ width: '150px', display: 'inline' }}
              value={props.zipcode || ''}
              className={makeValidationClass(props.zipcode)}
              onChange={e => props.onChange('Zipcode', e.target.value)}
            />
          </div>
        </label>
      </div>
      <label className="bp3-label Select-control-off" style={{ maxWidth: '246px' }}>
        Country
        <CountrySelect value={props.country} onChange={e => props.onChange('Country', e.target.value)} />
      </label>
    </div>
  )
}
